import type { TourStep } from '@/components/ui/Tour';
import {
  ACADEMY_PAGE_TOUR_STEP_IDS,
  CAMP_MANAGEMENT_TOUR_STEP_IDS,
  TERM_MANAGEMENT_TOUR_STEP_IDS,
  TOUR_CENTERED_STEP_SELECTOR,
} from '../tour-constants';
import { TourStepContent } from './StepContent';

export const academyProfileTourSteps: TourStep[] = [
  {
    content: (
      <TourStepContent
        title="Organization profile"
        description="Add contact details, description, and branding so families know who you are before they book."
      />
    ),
    selectorId: ACADEMY_PAGE_TOUR_STEP_IDS.PROFILE_TABS,
    position: 'bottom',
  },
  {
    content: (
      <TourStepContent
        title="Save your details"
        description="Update organization contact details, phone, email, and address. Academy name and age groups live under Academy settings."
      />
    ),
    selectorId: ACADEMY_PAGE_TOUR_STEP_IDS.PROFILE_FORM,
    position: 'top',
  },
];

export const academyPublicSiteTourSteps: TourStep[] = [
  {
    content: (
      <TourStepContent
        title="Public site URL"
        description="Choose path or branded subdomain mode, set your public slug, and preview how families reach your organization page."
      />
    ),
    selectorId: ACADEMY_PAGE_TOUR_STEP_IDS.PUBLIC_SITE_URL,
    position: 'bottom',
  },
  {
    content: (
      <TourStepContent
        title="Directory listing"
        description="Control whether your organization appears on the CourtSync public directory when you want more discovery."
      />
    ),
    selectorId: ACADEMY_PAGE_TOUR_STEP_IDS.PUBLIC_SITE_DIRECTORY,
    position: 'bottom',
  },
  {
    content: (
      <TourStepContent
        title="Branding & colors"
        description="Set primary and secondary colors used on your white-label public organization page."
      />
    ),
    selectorId: ACADEMY_PAGE_TOUR_STEP_IDS.PUBLIC_SITE_BRANDING,
    position: 'bottom',
  },
  {
    content: (
      <TourStepContent
        title="Logo & banner"
        description="Upload images families see at the top of your public site — logo for nav and a wide banner hero."
      />
    ),
    selectorId: ACADEMY_PAGE_TOUR_STEP_IDS.PUBLIC_SITE_MEDIA,
    position: 'top',
  },
  {
    content: (
      <TourStepContent
        title="Save public site settings"
        description="Save when you are done — changes apply to your live public organization page."
      />
    ),
    selectorId: ACADEMY_PAGE_TOUR_STEP_IDS.PUBLIC_SITE_SAVE,
    position: 'top',
  },
];

export const academySportsTourSteps: TourStep[] = [
  {
    content: (
      <TourStepContent
        title="Sports you offer"
        description="Turn on every racquet sport your academy teaches and choose one primary sport for defaults."
      />
    ),
    selectorId: ACADEMY_PAGE_TOUR_STEP_IDS.SPORTS_LIST,
    position: 'bottom',
  },
  {
    content: (
      <TourStepContent
        title="Save sports"
        description="After selecting sports, save so lessons, programs, and courts can be scoped correctly."
      />
    ),
    selectorId: ACADEMY_PAGE_TOUR_STEP_IDS.SPORTS_SAVE,
    position: 'top',
  },
];

export const academyProgramsTourSteps: TourStep[] = [
  {
    content: (
      <TourStepContent
        title="Development programs"
        description="Programs define pathways and skills. Link them to lessons for coach progress tracking."
      />
    ),
    selectorId: ACADEMY_PAGE_TOUR_STEP_IDS.PROGRAMS_NEW,
    position: 'bottom',
  },
  {
    content: (
      <TourStepContent
        title="Program list"
        description="Import a reference pathway or create custom stages. Programs can chain as player development steps."
      />
    ),
    selectorId: ACADEMY_PAGE_TOUR_STEP_IDS.PROGRAMS_LIST,
    position: 'top',
  },
];

export const academyLessonsTourSteps: TourStep[] = [
  {
    content: (
      <TourStepContent
        title="Your lesson calendar"
        description="This is your central schedule for group lessons, private packages, and camps. Use it to plan sessions, spot gaps, and open lesson details when you need to make changes."
      />
    ),
    selectorId: TOUR_CENTERED_STEP_SELECTOR,
  },
  {
    content: (
      <TourStepContent
        title="Calendar views"
        description="Switch between monthly, weekly, day, and list views to manage your lesson schedule."
      />
    ),
    selectorId: ACADEMY_PAGE_TOUR_STEP_IDS.CALENDAR_VIEW,
    position: 'bottom',
  },
  {
    content: (
      <TourStepContent
        title="Quick Lesson"
        description="Create a lesson on the spot—group, private, or camp. Set coaches, courts, capacity, and pricing. Use Terms and Camps for structured scheduling."
      />
    ),
    selectorId: ACADEMY_PAGE_TOUR_STEP_IDS.CALENDAR_CREATE,
    position: 'bottom',
  },
  {
    content: (
      <TourStepContent
        title="Filters"
        description="Filter the calendar by coach, student, sport, or lesson type to find sessions quickly."
      />
    ),
    selectorId: ACADEMY_PAGE_TOUR_STEP_IDS.CALENDAR_FILTERS,
    position: 'top',
  },
];

export const academyPaymentsTourSteps: TourStep[] = [
  {
    content: (
      <TourStepContent
        title="Payments area"
        description="Configure how families pay—Stripe Connect for online checkout or manual collection instructions on Starter."
      />
    ),
    selectorId: ACADEMY_PAGE_TOUR_STEP_IDS.PAYMENTS_TABS,
    position: 'bottom',
  },
  {
    content: (
      <TourStepContent
        title="Payment settings"
        description="Complete Stripe onboarding or add payment instructions guardians see at booking time."
      />
    ),
    selectorId: ACADEMY_PAGE_TOUR_STEP_IDS.PAYMENTS_SETTINGS,
    position: 'top',
  },
];

export {
  termCreateTourSteps,
  termLessonCreateTourSteps,
  termListTourSteps,
  termManageTourSteps,
} from '../term-management-tour-steps';
export { campManagementTourSteps as academyCampsTourSteps } from '../camp-management-tour-steps';

export const academyTermsTourStepIds = TERM_MANAGEMENT_TOUR_STEP_IDS;
export const academyCampsTourStepIds = CAMP_MANAGEMENT_TOUR_STEP_IDS;
